/**
 * PublicLayout — minimal customer portal chrome.
 *
 * After the Phase 6 retirement of the 9 hardcoded React themes, this
 * layout's old job (mirroring the active tenant theme on /preview/* sub-
 * pages) became obsolete — the actual public site lives at /s/:subdomain
 * and is rendered through SiteRouter → SiteRouterV2 (token-driven blocks).
 *
 * The only remaining consumer of <PublicLayout /> is the logged-in
 * customer self-service portal mounted at /account/* (bookings, quotes,
 * invoices, waivers, profile). That surface is a dashboard, not a
 * marketing page — it doesn't need theme chrome. So the layout is now a
 * thin shell with a tenant-aware header (logo + business name + phone)
 * and a clean footer, plus the site-wide language toggle.
 */
import React from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";

import { usePublicSite } from "../contexts/PublicSiteContext";
import { useTenantHreflang } from "../hooks/useTenantHreflang";
import { computeTenantSEO } from "../lib/tenantSeo";
import { LoadingSpinner } from "../components/common";
import SEOHead from "../components/SEOHead";
import SiteWidgetRenderer from "../components/public/SiteWidgetRenderer";
import PublicLangToggle from "../components/public/PublicLangToggle";
import AutoLangRedirect from "../components/public/AutoLangRedirect";

function LangWidgets() {
  return (
    <>
      <AutoLangRedirect />
      <div className="fixed bottom-4 right-4 z-[60] shadow-lg rounded-full">
        <PublicLangToggle />
      </div>
    </>
  );
}

export default function PublicLayout() {
  const { branding, micrositeId, loading, tenantStatus } = usePublicSite();
  const location = useLocation();
  const { lang: pageLang, alternates: pageAlternates } = useTenantHreflang();

  if (loading) return <LoadingSpinner />;

  // Suspended / cancelled / trial-expired tenants: hard gate.
  if (tenantStatus?.gate) {
    const SiteSuspended = require("../components/SiteSuspended").default;
    return <SiteSuspended message={tenantStatus.message} />;
  }

  const banner = tenantStatus?.banner || null;

  const bizName = branding?.business_name || "Party Rental";
  const bizLogo = branding?.logo_url || "";
  const seoCity = branding?.city || "";
  const seoState = branding?.state || "";
  const seoLocation = seoCity && seoState ? `${seoCity}, ${seoState}` : seoCity || seoState || "";

  const { favicon, appleTouchIcon, ogImage, localBusinessSchema } = computeTenantSEO(branding);

  const seoTitle = `My Account — ${bizName}`;
  const seoDesc = `Manage your bookings, quotes, invoices, and profile with ${bizName}${seoLocation ? ` (${seoLocation})` : ""}.`;

  return (
    <div
      className="min-h-screen flex flex-col bg-slate-50 text-slate-900"
      data-testid="public-layout"
    >
      <SEOHead
        title={seoTitle}
        description={seoDesc}
        ogImage={ogImage || bizLogo}
        favicon={favicon}
        appleTouchIcon={appleTouchIcon}
        lang={pageLang}
        alternates={pageAlternates}
        structuredData={localBusinessSchema ? [localBusinessSchema] : undefined}
        // The customer portal should never be indexed.
        noindex
      />

      {banner && (
        <div
          className="bg-amber-500 text-white text-center py-2 px-4 text-sm font-medium"
          data-testid="site-banner"
        >
          {banner}
        </div>
      )}

      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3" data-testid="public-layout-home">
            {bizLogo ? (
              <img src={bizLogo} alt={bizName} className="h-9 w-9 rounded-md object-contain" />
            ) : (
              <span className="h-9 w-9 rounded-md bg-slate-900 text-white grid place-items-center text-sm font-bold">
                {bizName.charAt(0).toUpperCase()}
              </span>
            )}
            <span className="font-semibold text-slate-900 truncate max-w-[260px]">{bizName}</span>
          </Link>
          <div className="hidden md:flex items-center gap-4 text-sm text-slate-500">
            {branding?.phone && (
              <a href={`tel:${branding.phone}`} className="flex items-center gap-1.5 hover:text-slate-900">
                <Phone className="h-3.5 w-3.5" /> {branding.phone}
              </a>
            )}
            {branding?.email && (
              <a href={`mailto:${branding.email}`} className="flex items-center gap-1.5 hover:text-slate-900">
                <Mail className="h-3.5 w-3.5" /> {branding.email}
              </a>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 flex flex-col" data-testid={`public-layout-route-${location.pathname.replace(/\W+/g, "-")}`}>
        <Outlet />
      </main>

      <footer className="bg-white border-t border-slate-200 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-sm text-slate-500 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-700">{bizName}</span>
            {seoLocation && (
              <span className="flex items-center gap-1 text-xs text-slate-400">
                <MapPin className="h-3 w-3" /> {seoLocation}
              </span>
            )}
          </div>
          <div className="text-xs text-slate-400">
            &copy; {new Date().getFullYear()} {bizName}. All rights reserved.
          </div>
        </div>
      </footer>

      {micrositeId && <SiteWidgetRenderer micrositeId={micrositeId} accentColor="#0f766e" />}
      <LangWidgets />
    </div>
  );
}
